import { Edit2, Printer } from 'lucide-react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { computar, fmtBRL, fmtUSD, fmtPct, fmtDateBR, diffDays, todayISO } from '../lib/cambio-calc';

export function CambioDetailsModal({ open, onClose, fechamento, onEdit }) {
  if (!fechamento) return null;
  const d = computar(fechamento);
  const diasRest = diffDays(todayISO(), fechamento.dt_vencimento);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={fechamento.codigo_unico}
      subtitle={`${fechamento.empresa_nome} · ${fmtDateBR(fechamento.dt_inicio)} → ${fmtDateBR(fechamento.dt_vencimento)} · ${d.dc} dias`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Fechar</Button>
          <Button variant="secondary" icon={Printer} onClick={() => window.print()}>Imprimir</Button>
          <Button icon={Edit2} onClick={() => { onEdit?.(fechamento); onClose?.(); }}>Editar</Button>
        </>
      }
    >
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 18,
      }}>
        <div>
          {/* Operação */}
          <Card title="Operação">
            <KV grid={3}>
              <Item lbl="Tipo de operação" val={({
                ndf:'NDF', cambio_pronto:'Câmbio pronto', trava:'Trava',
                d0:'D0', d1:'D1', outro:'Outro',
              })[fechamento.tipo_operacao] || 'NDF'} txt />
              <Item lbl="Empresa"     val={fechamento.empresa_nome} txt />
              <Item lbl="CNPJ"        val={fechamento.empresa_cnpj || '—'} />
              <Item lbl="Cliente"     val={fechamento.cliente_final || '—'} txt />
              <Item lbl="Operador"    val={fechamento.operador || '—'} txt />
              <Item lbl="Data início" val={fmtDateBR(fechamento.dt_inicio)} />
              <Item lbl="Vencimento"  val={fmtDateBR(fechamento.dt_vencimento)} />
              <Item lbl="DU / DC"     val={`${d.du} úteis · ${d.dc} corridos`} />
              {fechamento.observacao && (
                <Item lbl="Observação" val={fechamento.observacao} txt span={3} />
              )}
            </KV>
          </Card>

          {/* Câmbio */}
          <Card title="Câmbio">
            <KV grid={3}>
              <Item lbl="Principal USD"   val={`US$ ${fmtUSD(d.princ_usd, 0)}`} big />
              <Item lbl="Spot (R$/US$)"   val={fmtBRL(d.spot, 4)} big />
              <Item lbl="Princ. R$"       val={`R$ ${fmtBRL(d.princ_brl)}`} big />
              <Item lbl="CDI atual"       val={`${(Number(d.cdi) * 100).toFixed(2)}% a.a.`} />
              <Item lbl="SOFR/Libor"      val={`${(Number(d.sofr) * 100).toFixed(2)}% a.a.`} />
              <Item lbl="Spread banco"    val={`${d.spread_bps || 0} BPS`} />
            </KV>
          </Card>

          {/* Cálculos derivados — só pra NDF (câmbio pronto/trava não tem) */}
          {(fechamento.tipo_operacao || 'ndf') === 'ndf' && (
            <Card title="Cálculos derivados (NDF)">
              <KV grid={3}>
                <Item lbl="Juros real BRL"      val={fmtPct(d.jrs_brl)} />
                <Item lbl="Juros USD"           val={fmtPct(d.jrs_usd)} />
                <Item lbl="Taxa implícita"      val={fmtPct(d.tx_impl)} />
                <Item lbl="Futuro teórico"      val={fmtBRL(d.futuro_teorico, 4)} />
                <Item lbl="Prêmio bruto"        val={`R$ ${fmtBRL(d.premio_bruto)}`} />
                <Item lbl={`IR (${(d.ir_aliq * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)`} val={`R$ ${fmtBRL(d.ir_val)}`} />
                <Item lbl="Prêmio líquido"      val={`R$ ${fmtBRL(d.premio_liquido)}`} primary />
                <Item lbl="Despesa"             val={`R$ ${fmtBRL(d.despesa)}`} />
              </KV>
            </Card>
          )}
        </div>

        {/* Sidebar: resultado */}
        <div>
          <div style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 14,
            padding: 22,
          }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '.14em',
              textTransform: 'uppercase', color: 'var(--primary)',
              background: 'var(--primary-soft, rgba(59,228,118,0.12))',
              padding: '3px 10px', borderRadius: 99,
              display: 'inline-block', marginBottom: 14,
            }}>Resultado final</span>

            <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 600 }}>Total a Crédito</div>
              <div className="font-display" style={{
                fontSize: 26, fontWeight: 600,
                fontVariantNumeric: 'tabular-nums', margin: '4px 0',
              }}>R$ {fmtBRL(d.total_credito)}</div>
            </div>

            <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 600 }}>Taxa Líquida Final</div>
              <div className="font-display" style={{
                fontSize: 32, fontWeight: 600, color: 'var(--primary)',
                fontVariantNumeric: 'tabular-nums', margin: '4px 0',
              }}>{fmtBRL(d.taxa_final, 4)}</div>
              <div className="font-mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>R$ por US$ 1,00</div>
            </div>

            <div style={{ padding: '14px 0' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 600, marginBottom: 8 }}>Status</div>
              <StatusPill status={fechamento.status} diasRest={diasRest} />
              <div style={{ fontSize: 11.5, color: 'var(--ink-2)', marginTop: 8 }}>
                {fechamento.status === 'aberto' && diasRest >= 0
                  ? `Vence em ${diasRest} dia${diasRest === 1 ? '' : 's'}`
                  : fechamento.status === 'aberto'
                    ? 'Vencido — aguardando atualização'
                    : `Liquidado em ${fmtDateBR(fechamento.dt_vencimento)}`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── helpers visuais ──────────────────────────────────────────────
function Card({ title, children }) {
  return (
    <div className="card" style={{ padding: 22, marginBottom: 14 }}>
      <div style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '.14em',
        textTransform: 'uppercase', color: 'var(--muted)',
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
      }}>
        <span>{title}</span>
        <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>
      {children}
    </div>
  );
}

function KV({ children, grid = 3 }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: `repeat(${grid}, 1fr)`,
      gap: '14px 18px',
    }}>{children}</div>
  );
}

function Item({ lbl, val, txt, big, primary, span }) {
  return (
    <div style={span ? { gridColumn: `span ${span}` } : undefined}>
      <div style={{
        fontSize: 10.5, color: 'var(--muted)', textTransform: 'uppercase',
        letterSpacing: '.08em', fontWeight: 600, marginBottom: 4,
      }}>{lbl}</div>
      <div style={{
        fontSize: big ? 20 : 14.5,
        color: primary ? 'var(--primary)' : 'var(--ink)',
        fontWeight: big ? 600 : 500,
        fontFamily: txt ? 'inherit' : '"JetBrains Mono", monospace',
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: big ? '-0.01em' : undefined,
      }}>{val}</div>
    </div>
  );
}

function StatusPill({ status, diasRest }) {
  let cls, lbl, color, bg;
  if (status === 'aberto' && diasRest >= 0 && diasRest <= 7) {
    cls = 'vence'; lbl = `Vence em ${diasRest}d`; color = '#f59e0b'; bg = 'rgba(245,158,11,0.12)';
  } else if (status === 'aberto') {
    cls = 'aberto'; lbl = 'Em aberto'; color = '#3be476'; bg = 'rgba(59,228,118,0.10)';
  } else if (status === 'liquidado') {
    cls = 'liquidado'; lbl = 'Liquidado'; color = '#60a5fa'; bg = 'rgba(96,165,250,0.10)';
  } else {
    cls = 'cancelado'; lbl = 'Cancelado'; color = 'var(--muted)'; bg = 'rgba(122,129,140,0.18)';
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 10px', borderRadius: 99,
      fontSize: 11, fontWeight: 600, letterSpacing: '.03em',
      background: bg, color, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
      {lbl}
    </span>
  );
}
